import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import posthog from 'posthog-js';
import { PostHogErrorBoundary, PostHogProvider } from '@posthog/react';

// Session replay should only run on the deployed app — never on local dev, so
// we don't burn recording quota or capture dev noise while working locally.
const isLocalhost =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '0.0.0.0', '[::1]'].includes(window.location.hostname);

// Public client token + host — safe to ship in the bundle. The CI build does
// NOT inject these env vars (deploy workflow only sets API_BASE_URL/ENV), so
// the hardcoded fallback is what makes PostHog work in production.
posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY || 'phc_nagT5phA8ShnQ67D7VBYgL7HVuxE5VKZ4Uzgu3v84xJA', {
  api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
  defaults: '2026-01-30',
  // Share the distinct_id cookie across *.rubrichq.io so an anonymous visitor
  // from the marketing site (rubrichq.io) stitches to the user we identify
  // here (app.rubrichq.io) — same PostHog project token on both surfaces.
  cross_subdomain_cookie: true,
  // Only record sessions on the deployed app, not on localhost.
  disable_session_recording: isLocalhost,
});

// Fallback for PostHogErrorBoundary. Without this, the boundary renders NOTHING on
// any render error → the app silently white-screens (and the error is only visible in
// the console/session-replay). This surfaces the error on-page + offers a reload, so a
// crash in one page never blanks the whole app.
function AppErrorFallback({ error, componentStack }) {
  const message = error?.message || String(error || 'Unknown error');
  return (
    <div style={{ maxWidth: 820, margin: '48px auto', padding: 24, fontFamily: 'system-ui, sans-serif', color: '#111' }}>
      <h1 style={{ fontSize: 20, margin: '0 0 8px' }}>Something went wrong</h1>
      <p style={{ color: '#555', margin: '0 0 16px' }}>
        This page hit an error and couldn't render. Reloading usually fixes it.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: '8px 16px', background: '#111', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
      >
        Reload page
      </button>
      <details style={{ marginTop: 20 }} open>
        <summary style={{ cursor: 'pointer', color: '#b91c1c', fontWeight: 600 }}>Error details</summary>
        <pre style={{ marginTop: 8, padding: 12, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 12 }}>
          {message}{error?.stack ? '\n\n' + error.stack : ''}{componentStack ? '\n\nComponent stack:' + componentStack : ''}
        </pre>
      </details>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <PostHogErrorBoundary fallback={AppErrorFallback}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </PostHogErrorBoundary>
    </PostHogProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
